import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import {
  SBOM_PORTAL_PRIVATE,
  SNIPPET_DETECTION,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../../shared/featureFlags/flagNames';
import { FilterKeys, isProjects, isSharedProjects } from '../../../../../shared/projectsV2';
import { isFeatureEnabled, useIsFeatureEnabled } from '../../../../helpers/features/featureFlags';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import { FilterPanel } from '../../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button';
import {
  SubscriptionBannerProjectsPageContainer,
} from '../../../settings/subscription/banners/SubscriptionBannerProjectsPage';
import useFetchProjectsAction from '../hooks/useFetchProjectsAction';
import type { ProjectsFilter } from '../../../../../shared/projectsV2';
import type { MultiSelectFilterOption } from '../../../generic/Filter/MultiSelectFilter';

import ProjectInventory from './ProjectInventory';
import ProjectLabels from './ProjectLabels';
import ProjectType from './ProjectType';
import ProjectVisibility from './ProjectVisibility';
import Scanned from './Scanned';

const ProjectsFilters: VoidFunctionComponent = () => {
  const filter = useSelector(ProjectsV2Store.selectors.getFilter) as ProjectsFilter;
  const category = useSelector(ProjectsV2Store.selectors.getCategory);

  const resetAllFilters = useFetchProjectsAction(ProjectsV2Store.actions.resetAllFilters);
  const setFilter = useFetchProjectsAction(ProjectsV2Store.actions.setFilter);

  // disable "reset all filters" link if no filters are active
  const hasFilterApplied = useSelector(ProjectsV2Store.selectors.getHasFilterApplied);
  const shouldDisplayProjectVisibility = isProjects(category) || isFeatureEnabled(SBOM_PORTAL_PRIVATE);

  const isSnippetsEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isVendoredEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const shouldDisplayProjectInventory = isProjects(category) && (isSnippetsEnabled || isVendoredEnabled);

  return (
    <div className="projects-filters">
      <SubscriptionBannerProjectsPageContainer />
      <br />
      <div className="projects-filters-header">
        <span className="projects-filters-header-title">Filters</span>
        <Button
          type={BUTTON_TYPE.TEXT}
          size={BUTTON_SIZE.SMALL}
          onClick={() => hasFilterApplied && resetAllFilters()}
          isDisabled={!hasFilterApplied}
        >
          Reset all filters
        </Button>
      </div>
      <div className="projects-filters__content">
        {isProjects(category) && <ProjectType />}
        {shouldDisplayProjectInventory && (
          <ProjectInventory isSnippetsEnabled={isSnippetsEnabled} isVendoredEnabled={isVendoredEnabled} />
        )}
        {shouldDisplayProjectVisibility && <ProjectVisibility />}
        {(isProjects(category) || isSharedProjects(category)) && (
          <FilterPanel>
            <ProjectLabels
              filterLabels={filter.labels ?? []}
              onChange={(items: MultiSelectFilterOption[]) => {
                setFilter({ ...filter, [FilterKeys.Labels]: items.map((i) => i.value) });
              }}
            />
          </FilterPanel>

        )}
        <Scanned />
      </div>
    </div>
  );
};

export default ProjectsFilters;
